// @flow

import React from 'react';
import { Box, Text, ThemeContext } from 'grommet';
import { RoutedButton } from 'speed-js-react-web';
import permissionRequired from 'esm-web-pro/decorators/permissionRequired';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import SIP_REQUIRED_PERMISSION from 'esm-web-pro/containers/panels/calling/constants';

type Props = {
    location: string,
}

const messages = defineMessages({
    infrastructure: {
        id: 'adminpage-networks',
        defaultMessage: 'Infrastructure',
    },
    addresses: {
        id: 'adminpage-addresses',
        defaultMessage: 'Addresses',
    },
    authorization: {
        id: 'adminpage-authorization',
        defaultMessage: 'Authorization',
    },
    nomenclatures: {
        id: 'adminpage-nomenclature-label-plural',
        defaultMessage: 'Nomenclatures',
    },
    attributes: {
        id: 'adminpage-attributes',
        defaultMessage: 'Attributes',
    },
    exports: {
        id: 'adminpage-export',
        defaultMessage: 'Exports',
    },
    audit: {
        id: 'adminpage-audit',
        defaultMessage: 'Audit',
    },
    locks: {
        id: 'adminpage-locks',
        defaultMessage: 'Locks',
    },
});

const AdminPageTheme = {
    button: {
        border: {
            radius: '0',
            color: 'none',
        },
        size: 'medium',
    },
    active: {
        background: {
            color: 'white',
        },
    },
};

const checkIfActive = (url, path) => {
    const urlArr = url.split('/');
    const pathArr = path.split('/');

    let isIncluded = true;

    urlArr.forEach((part, index) => {
        if (part !== pathArr[index]) {
            isIncluded = false;
        }
    });

    return isIncluded;
};

const PermissionsRequiredButton = permissionRequired()(RoutedButton);
const PermissionRequiredSection = permissionRequired()(Box);

const AdminSideMenuLinks = React.memo((props: Props) => {
    const { location } = props;

    const { formatMessage } = useIntl();

    const menuLinks = [
        {
            title: formatMessage(messages.infrastructure),
            permissions: ['networks.view_infrastructureprovider'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-network-providers" defaultMessage="Providers" />,
                    link: '/administration/infrastructure/providers',
                    permissions: ['networks.view_infrastructureprovider'],
                },
                {
                    name: <FormattedMessage
                        id="adminpage-infrastructure-cable-models"
                        defaultMessage="Cable Models"
                    />,
                    link: '/administration/infrastructure/cable-models',
                    permissions: ['networks.view_infrastructureprovider'],
                },
            ],
        },
        {
            title: formatMessage(messages.addresses),
            permissions: ['addresses.view_map'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-addresses-map" defaultMessage="Maps" />,
                    link: '/administration/maps/maps',
                    permissions: [
                        'addresses.add_map', 'addresses.change_map', 'addresses.view_map', 'addresses.delete_map',
                    ],
                },
            ],
        },
        {
            title: formatMessage(messages.authorization),
            permissions: ['authorisation.view_user', 'authorisation.view_role', 'auth.view_permission',
                'auth.view_group'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-authorization-users" defaultMessage="Users" />,
                    link: '/administration/authorisation/users',
                    permissions: ['authorisation.view_user'],
                },
                {
                    name: <FormattedMessage id="adminpage-authorization-role" defaultMessage="Roles" />,
                    link: '/administration/authorisation/roles',
                    permissions: ['authorisation.view_role'],
                },
                {
                    name: <FormattedMessage id="adminpage-authorization-permissions" defaultMessage="Permissions" />,
                    link: '/administration/authorisation/permissions',
                    permissions: ['auth.view_permission'],
                },
                {
                    name: <FormattedMessage id="adminpage-authorization-groups" defaultMessage="Groups" />,
                    link: '/administration/authorisation/groups',
                    permissions: ['auth.view_group'],
                },
            ],
        },
        {
            title: formatMessage(messages.nomenclatures),
            permissions: ['nomenclatures.add_nomenclatureitem', 'nomenclatures.change_nomenclatureitem',
                'nomenclatures.delete_nomenclatureitem', 'nomenclatures.view_nomenclatureitem',
                'nomenclatures.add_nomenclaturetype', 'nomenclatures.delete_nomenclaturetype',
                'nomenclatures.view_nomenclaturetype'],
            children: [
                {
                    name: (
                        <FormattedMessage
                            id="adminpage-nomenclature-nomenclature-type"
                            defaultMessage="Nomenclature type"
                        />
                    ),
                    link: '/administration/nomenclatures/types',
                    permissions: ['nomenclatures.add_nomenclatureitem', 'nomenclatures.change_nomenclatureitem',
                        'nomenclatures.delete_nomenclatureitem', 'nomenclatures.view_nomenclatureitem',
                        'nomenclatures.add_nomenclaturetype', 'nomenclatures.delete_nomenclaturetype',
                        'nomenclatures.view_nomenclaturetype'],
                },
            ],
        },
        {
            title: formatMessage(messages.exports),
            permissions: ['audit.view_auditeventtype', 'audit.delete_auditeventtype',
                'audit.change_auditeventtype', 'audit.add_auditeventtype', 'audit.view_auditevent',
                'audit.delete_auditevent'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-export" defaultMessage="Exports" />,
                    link: '/administration/exports/eit',
                    permissions: ['audit.view_auditevent', 'audit.delete_auditevent',
                        'audit.change_auditevent', 'audit.add_auditevent'],
                },
            ],
        },
        {
            title: formatMessage(messages.audit),
            permissions: ['audit.view_auditeventtype', 'audit.delete_auditeventtype',
                'audit.change_auditeventtype', 'audit.add_auditeventtype', 'audit.view_auditevent',
                'audit.delete_auditevent'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-audit-event" defaultMessage="Audit event" />,
                    link: '/administration/audits/events',
                    permissions: ['audit.view_auditevent', 'audit.delete_auditevent',
                        'audit.change_auditevent', 'audit.add_auditevent'],
                },
                {
                    name: <FormattedMessage id="adminpage-audit-event-type" defaultMessage="Audit event type" />,
                    link: '/administration/audits/eventtypes',
                    permissions: ['audit.view_auditeventtype', 'audit.delete_auditeventtype',
                        'audit.change_auditeventtype', 'audit.add_auditeventtype'],
                },
            ],
        },
        {
            title: formatMessage(messages.locks),
            permissions: ['locks.view_objectlock', 'locks.delete_objectlock'],
            children: [
                {
                    name: <FormattedMessage id="adminpage-locks" defaultMessage="Locks" />,
                    link: '/administration/locks/object',
                    permissions: ['locks.view_objectlock'],
                },
            ],
        },
    ];

    return (
        <Box overflow="auto">
            <ThemeContext.Extend value={AdminPageTheme}>
                <Box
                    justify="center"
                    border={{ side: 'right', color: '#CCCCCC' }}
                    flex={{ shrink: 0 }}
                    pad={{ bottom: 'small' }}
                    background="backgroundColor"
                    style={{ zIndex: 1, marginBottom: '30px', minWidth: 200 }}
                >
                    {menuLinks.map((value) => (
                        <React.Fragment key={`${value.title}`}>
                            <PermissionRequiredSection
                                plain
                                margin={{
                                    top: 'small', bottom: 'xsmall', left: 'small', right: 'small',
                                }}
                                permissions={value.permissions}
                                flex={{ shrink: 0 }}
                            >
                                <Text>
                                    {value.title}
                                </Text>
                            </PermissionRequiredSection>
                            {
                                value.children.map((val) => (
                                    <PermissionsRequiredButton
                                        plain
                                        active={checkIfActive(val.link, location.pathname)}
                                        key={`${val.link}`}
                                        hoverIndicator="light-2"
                                        label={(
                                            <Text
                                                size="12px"
                                                margin={{ left: '30px' }}
                                                style={{ display: 'block' }}
                                            >
                                                {val.name}
                                            </Text>
                                        )}
                                        to={val.link}
                                        permissions={val.permissions}
                                    />
                                ))
                            }
                        </React.Fragment>
                    ))}
                </Box>
            </ThemeContext.Extend>
        </Box>
    );
});
export default AdminSideMenuLinks;
